import { ErrorHandler, Injectable } from "@angular/core";
import {
  combineLatest,
  defer,
  EMPTY,
  firstValueFrom,
  merge,
  Observable,
  of,
  Subject,
  throwError,
} from "rxjs";
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  scan,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
} from "rxjs/operators";
import { SharedApiV2 } from "@capstone/mock-api";
import { UserService } from "src/app/core/auth";
import { SiteAvailabilityStatus } from "src/app/core/constants";
import { RequestV2Service } from "src/app/core/request-v2.service";
import { RequestService } from "src/app/core/request.service";
import { UserPreferencesService } from "src/app/core/user-preferences.service";
import { Partner } from "src/app/partner/global/partner.model";
import { PartnersService } from "src/app/partner/global/partners.service";
import {
  ColumnSort,
  firstExistentValueFrom,
  getDefaultUpdater,
  isExistent,
  isInstanceOf,
  isNot,
  mapNullable,
  ODataResourceUrl,
  PartnerResource,
  pauseable,
  SortOrder,
} from "src/utils";
import { PaginationV2 } from "../pagination-v2";
import { SiteAppointmentsSettingsUpdate } from "./site-appointments-settings-update.model";
import { SiteGeneralSettingsUpdate } from "./site-general-settings-update.model";
import { SiteHelpAssistSettingsUpdate } from "./site-help-assist-settings-update.model";
import { SiteListItem } from "./site-list-item.model";
import { SiteListQueryModel } from "./site-list-query.model";
import { SiteSummary, sortSiteSummaries } from "./site-summary.model";
import { SiteUpdate } from "./site-update.model";
import { Site, SiteReference, sitesResource } from "./site.model";

export const STORED_SELECTED_SITE_KEY = "SelectedSite";

@Injectable({ providedIn: "root" })
export class SitesService {
  public constructor(
    private readonly errorHandler: ErrorHandler,
    private readonly partners: PartnersService,
    private readonly request: RequestService,
    private readonly requestV2: RequestV2Service,
    private readonly user: UserService,
    private readonly userPreferences: UserPreferencesService,
  ) {}

  /** Pushes a freshly loaded site to whoever is showing the selected site. */
  private readonly detailUpdates = new Subject<Site>();

  /** Pushes a freshly loaded site into the site list so the row stays in sync. */
  private readonly summaryUpdates = new Subject<Site>();

  /** The selection whose details failed, so waiters settle on that identity. */
  private readonly detailFailures = new Subject<Pick<Site, "id" | "partner">>();

  // The site list, one entry per partner. A new `/partnersSites` response
  // rebuilds the whole thing. A single site only updates a row if we still
  // have the same partner object it came from, otherwise it's out of date.
  private readonly summaryStateChanges = merge(
    this.partners.changes,
    this.summaryUpdates,
  ).pipe(
    scan((state: SummaryState, event): SummaryState => {
      if (event instanceof Site) {
        return state.loadedPartners.has(event.partner)
          ? {
              loadedPartners: state.loadedPartners,
              sitesByPartner: applyConfirmedSite(state.sitesByPartner, event),
            }
          : state;
      }

      const sitesByPartner = new Map<string, readonly SiteSummary[]>();
      for (const partner of event) {
        sitesByPartner.set(
          getPartnerRoleKey(partner),
          SiteSummary.deserializeList(partner.sites, { partner }),
        );
      }
      return { loadedPartners: new Set(event), sitesByPartner };
    }, emptySummaryState),
    shareReplay(1),
  );

  private readonly availableSummaryChanges =
    this.getSiteSummariesChanges().pipe(shareReplay(1));

  private readonly maybeSelectedForPartnerUserChanges = combineLatest([
    this.availableSummaryChanges,
    this.userPreferences.siteIdChanges,
  ]).pipe(
    map(([sites, preferredSiteId]) => {
      if (!this.user.isPartner()) {
        throw new Error(
          'Partner user "selected site" observable used for wrong user type.',
        );
      }

      if (!sites) {
        return "loading";
      }

      if (sites.length === 0) {
        return "list-empty";
      }

      const storedSelectedSite = sessionStorage.getItem(
        STORED_SELECTED_SITE_KEY,
      );

      if (storedSelectedSite) {
        const matchedSite = sites.find(
          (s) => s.id.toString() === storedSelectedSite,
        );

        if (matchedSite) {
          return matchedSite;
        }
      }

      const preferredOrDefaultSite =
        (preferredSiteId && sites.find((s) => s.id === preferredSiteId)) ||
        sites[0];

      // If there is no stored site in the sessionStorage then update the sessionStorage with the first site
      this.updateStoredSelectedSiteInSession(preferredOrDefaultSite.id);
      return preferredOrDefaultSite;
    }),
    shareReplay(1),
  );

  private readonly maybeSelectedForGlobalUserChanges = combineLatest([
    this.userPreferences.siteIdChanges,
    this.availableSummaryChanges,
  ]).pipe(
    map(([id, sites]) => {
      if (this.user.isPartner()) {
        throw new Error(
          'Global user "selected site" observable used for wrong user type.',
        );
      }

      if (!sites) {
        return "loading";
      }

      if (sites.length === 0) {
        return "list-empty";
      }

      return sites.find((site) => site.id === id) ?? "not-found";
    }),
    shareReplay(1),
  );

  // `isPartner()` only gives the right answer after the user has loaded, so we
  // wait for that once and stick with the stream we pick.
  private readonly maybeSelectedForUserChanges = this.user.isLoadedChanges.pipe(
    take(1),
    switchMap(() =>
      this.user.isPartner()
        ? this.maybeSelectedForPartnerUserChanges
        : this.maybeSelectedForGlobalUserChanges,
    ),
  );

  private readonly maybeSelectedSummaryChanges =
    this.maybeSelectedForUserChanges.pipe(
      distinctUntilChanged(isSameSelection),
      shareReplay(1),
    );

  // The selected site, paired with the partner it came from, so that "is the
  // page still up" and "is the site loaded" both read from the same stream and
  // can't get out of order.
  private readonly selectedStateChanges = this.maybeSelectedSummaryChanges.pipe(
    switchMap((selected) =>
      selected instanceof SiteSummary
        ? merge(
            this.loadSiteDetails(selected.partner, selected.id).pipe(
              // Keep the shared streams alive but never stand in another site
              // for this one; waiters settle through `detailFailures`.
              catchError((error: unknown) => {
                this.errorHandler.handleError(error);
                this.detailFailures.next(selected);
                return EMPTY;
              }),
            ),
            this.detailUpdates.pipe(
              filter((site) => isSameSelectedSite(site, selected)),
            ),
          ).pipe(
            // Start with "loading" so callers never see the old site while we
            // fetch the new one.
            startWith("loading" as const),
            map((value) => ({
              partnerRoleKey: getPartnerRoleKey(selected.partner),
              value,
            })),
          )
        : of({ partnerRoleKey: "", value: selected }),
    ),
    shareReplay(1),
  );

  private readonly maybeSelectedChanges = this.selectedStateChanges.pipe(
    map(({ value }) => value),
  );

  public readonly selectedChanges = this.maybeSelectedChanges.pipe(
    filter(isInstanceOf(Site)),
    distinctUntilChanged(),
  );

  /**
   * The selected site's identity, emitting only when a different site or
   * partner is selected (not when the selected site settings are updated).
   */
  public readonly selectedSiteReferenceChanges: Observable<SiteReference> =
    this.selectedChanges.pipe(
      distinctUntilChanged<Site>(isSameSiteAndPartner),
      map(({ id, partner }) => ({ id, partner: { key: partner.key } })),
    );

  public readonly selectedResourceChanges = this.selectedChanges.pipe(
    map((site) => sitesResource.get(site.partner).appendId(site.id)),
  );

  /**
   * Whether the selected site has finished loading. Switching to another site
   * under the same partner keeps the nav, site picker and page on screen; only
   * the very first load and a partner change show the full-app loading state.
   */
  public readonly isSelectedLoadedChanges = this.selectedStateChanges.pipe(
    scan(
      (
        previous: SelectedSiteLoadState,
        { partnerRoleKey, value },
      ): SelectedSiteLoadState => ({
        partnerRoleKey,
        isLoaded:
          (value !== "loading" && value !== "not-found") ||
          (previous.isLoaded && previous.partnerRoleKey === partnerRoleKey),
      }),
      { partnerRoleKey: null, isLoaded: false },
    ),
    map(({ isLoaded }) => isLoaded),
    distinctUntilChanged(),
  );

  private readonly defaultSelectionChanges = this.availableSummaryChanges.pipe(
    filter(isExistent),
    // The list could be empty before initial partner set up or if the user
    // isn't configured with all their correct sites yet.
    map((sites) => sites[0] ?? null),
  );

  readonly #isSelectedLoadingChanges = this.isSelectedLoadedChanges.pipe(
    map(isNot(true)),
  );

  public readonly hasActiveSitesChanges = this.availableSummaryChanges.pipe(
    map((sites) => Boolean(sites && sites.length > 0)),
    shareReplay(1),
  );

  // When switching partners, the list of sites and the selected site both
  // reload, but the `selectedChanges` will retain the previous site until it
  // finishes loading, so there may be a moment when the old selected site
  // is still loaded once the new list of sites appears. This means that the
  // selected site doesn't exist in the site list which shouldn't ever happen.
  //
  // To avoid this, we can hold back the emission of the newly updated list
  // until after the new selected site has been loaded/selected as well.
  public readonly siteSummariesChanges = combineLatest([
    this.availableSummaryChanges,
    this.#isSelectedLoadingChanges,
  ]).pipe(
    map(([siteSummaries, isSelectedLoading]) =>
      isSelectedLoading ? null : siteSummaries,
    ),
    distinctUntilChanged(),
  );

  /** The site list row for the selected site, for display only. */
  public readonly selectedSiteSummaryChanges = combineLatest([
    this.getSiteSummariesChanges(),
    this.selectedChanges,
  ]).pipe(
    map(
      ([siteSummaries, selected]) =>
        siteSummaries?.find((summary) =>
          isSameSiteAndPartner(summary, selected),
        ) ?? null,
    ),
    distinctUntilChanged(),
  );

  private isSelectedLoadedNotifier?: Observable<boolean>;

  private readonly unloadNotifier = new Subject<void>();

  /**
   * Gets the short site entries a user is allowed to pick for a partner.
   * @param partner The partner to list sites for, defaulting to the selected
   * partner.
   */
  public getSiteSummariesChanges(
    partner?: Partner,
  ): Observable<readonly SiteSummary[] | null> {
    if (!partner) {
      return this.partners.selectedChanges.pipe(
        switchMap((selected) => this.getSiteSummariesChanges(selected)),
      );
    }
    return this.summaryStateChanges.pipe(
      map(
        (state) =>
          state.sitesByPartner.get(getPartnerRoleKey(partner)) ??
          emptySummaries,
      ),
      distinctUntilChanged(),
    );
  }

  private updateStoredSelectedSiteInSession(siteId: Site["id"]): void {
    sessionStorage.setItem(STORED_SELECTED_SITE_KEY, `${siteId}`);
  }

  public async load(): Promise<boolean> {
    if (!this.isSelectedLoadedNotifier) {
      // Skip emissions whenever the partner key has changed so that the
      // intermediate missing value (current site not available at new partner)
      // doesn't trigger a default selection update. See: #18940
      const readyMaybeSelectedChanges = this.maybeSelectedChanges.pipe(
        pauseable(
          this.userPreferences.partnerKeyChanges.pipe(skip(1)),
          this.availableSummaryChanges.pipe(filter(isExistent)),
        ),
      );
      const [updater, notifier] = getDefaultUpdater<
        Site | SiteSummary | "list-empty",
        SiteSummary
      >(readyMaybeSelectedChanges, this.defaultSelectionChanges);
      this.isSelectedLoadedNotifier = notifier;

      // Set the default site if one isn't selected or the selected wasn't found.
      updater
        .pipe(
          switchMap((site) => this.setSelected(site)),
          takeUntil(this.unloadNotifier),
        )
        .subscribe();
    }

    return firstValueFrom(
      merge(
        this.isSelectedLoadedNotifier,
        // Startup has to settle on a failed detail read, and that failure is
        // not the same answer as a partner with no sites.
        this.detailFailures.pipe(
          switchMap(({ id }) =>
            throwError(() => new Error(`Could not load site ${id}.`)),
          ),
        ),
      ),
    );
  }

  public getPaginatedListChanges({
    pagination = new PaginationV2(),
    sort = { key: "name", order: SortOrder.Ascending },
  }: {
    pagination?: PaginationV2;
    sort?: ColumnSort;
  } = {}): Observable<SiteListItem[] | null> {
    return this.partners.selectedChanges.pipe(
      switchMap((partner) =>
        this.requestV2
          .getPaged({
            prefix: partner.getApiUrl(),
            path: "/Sites/All",
            data: new SiteListQueryModel({ pagination, sort }),
            pagination,
          })
          .pipe(
            mapNullable((page) =>
              SiteListItem.deserializeList(page, { partner }),
            ),
          ),
      ),
    );
  }

  /** Pages of sites for one partner. The caller decides when to stop. */
  public getPaginatedPageChanges({
    partner,
    pagination,
    filter: siteFilter,
    sort = { key: "name", order: SortOrder.Ascending },
  }: {
    partner: Partner;
    pagination: PaginationV2;
    filter?: SharedApiV2.SitesFilterDto;
    sort?: ColumnSort;
  }): Observable<SharedApiV2.PagedList<SiteListItem> | null> {
    const data = new SiteListQueryModel({
      pagination,
      sort,
      filter: siteFilter,
    });
    return defer(() => {
      pagination.reset();
      return pagination.requestNotifier.pipe(
        startWith(1),
        switchMap(() =>
          this.requestV2.post({
            prefix: partner.getApiUrl(),
            path: "/Sites/All",
            data,
          }),
        ),
        mapNullable((page) => {
          // Update the pagination right away. `getPaged` does this later in an
          // async step, which can stomp on a newer search after we cancel.
          pagination.reset(page);
          return {
            ...page,
            items: SiteListItem.deserializeList(page, { partner }),
          };
        }),
      );
    });
  }

  public async updateSitePreferences(
    siteId: SiteReference["id"],
  ): Promise<void> {
    await this.userPreferences.setSiteId(siteId, {
      sessionOnly: this.user.isCarrier(),
    });
  }

  public async setSelected(site: Pick<Site, "id" | "partner">): Promise<void> {
    // Set the new partner first so that the default site doesn't reset the
    // partner back to the previous partner when it calls this. See: #18940
    // We also need to await at least the last one so that the notifier below
    // loads at the correct time.
    await this.partners.setSelected(site.partner);

    const previousSiteId = sessionStorage.getItem(STORED_SELECTED_SITE_KEY);
    sessionStorage.setItem(STORED_SELECTED_SITE_KEY, `${site.id}`);

    // Listen before persisting: the detail read can fail while the preference
    // write is still in flight. Waiting for this exact site also stops a
    // fallback selection reporting success for a site nobody asked for.
    const settled = this.isSelectedLoadedNotifier
      ? firstValueFrom(
          merge(
            this.selectedChanges.pipe(
              filter((selected) => isSameSiteAndPartner(selected, site)),
            ),
            this.detailFailures.pipe(
              filter((failed) => isSameSiteAndPartner(failed, site)),
              switchMap((failed) =>
                throwError(
                  () => new Error(`Could not load site ${failed.id}.`),
                ),
              ),
            ),
          ),
        )
      : null;
    // Keeps an early rejection handled until it is awaited below.
    settled?.catch(() => undefined);

    await this.updateSitePreferences(site.id);

    if (!settled) {
      return;
    }

    try {
      await settled;
    } catch (error: unknown) {
      // Leave a site the app can actually show selected.
      if (previousSiteId !== null) {
        sessionStorage.setItem(STORED_SELECTED_SITE_KEY, previousSiteId);
        await this.updateSitePreferences(Number(previousSiteId));
      }
      throw error;
    }
  }

  public async update(update: SiteUpdate): Promise<Site> {
    const id = update.base
      ? await this.patch(update.base.id, update)
      : await this.post(update);

    const site = await this.get(id, update.base?.partner);
    await this.refreshPartnerSitesIfChanged(site, update.base);
    return site;
  }

  /**
   * The API decides which sites a partner shows and who can see them, so after
   * adding a site or changing its status we have to reload the partners to find
   * out.
   */
  private async refreshPartnerSitesIfChanged(
    site: Site,
    base: Site | null,
  ): Promise<void> {
    if (base && site.status === base.status) {
      return;
    }
    try {
      await this.partners.refreshPartners();
    } catch (error: unknown) {
      // The write already committed, so rejecting here would report a failed
      // save and invite a retry that creates a second site.
      this.errorHandler.handleError(error);
    }
  }

  /**
   * Gets the concrete `ODataResourceUrl` for a `PartnerResource` that is nested
   * within the appropriate, currently selected partner as well as filtered by
   * the currently selected site.
   *
   * **Example result URL:**
   *
   * `https://dev-mr.capstonelogistics.com/api/partner/1234/docks?$filter=siteID eq 10`
   *
   * @param resource The partner resource to add the site filter to.
   * @param siteIdProperty The resource model property to match the site ID
   * against. Defaults to `siteID`.
   */
  public getResourceInSelected<Resource, ResourceName>(
    resource: PartnerResource<Resource, ResourceName>,
    siteIdProperty = "siteID",
  ): Observable<ODataResourceUrl<Resource, ResourceName>> {
    return this.selectedChanges.pipe(
      distinctUntilChanged(isSameSiteAndPartner),
      map((site) =>
        resource
          .get(site.partner)
          .addRawFilter(`${siteIdProperty} eq ${site.id}`),
      ),
    );
  }

  /**
   * Gets the concrete `ODataResourceUrl` for a `PartnerResource` that is nested
   * within the appropriate, currently selected partner. This is a convenience
   * pass-through method for `PartnersService.getResourceInSelected`.
   *
   * You'll often want this one rather than `SitesService.getResourceInSelected`
   * when modifying resources (`POST`, `PATCH`, `DELETE`) or when selecting a
   * specific resource by ID as filtering by site on these is invalid.
   *
   * **Example result URL:**
   *
   * `https://dev-mr.capstonelogistics.com/api/partner/1234/docks`
   *
   * @param resource The partner resource to add the partner to.
   */
  public getResourceInSelectedPartner<Resource, ResourceName>(
    resource: PartnerResource<Resource, ResourceName>,
  ): Observable<ODataResourceUrl<Resource, ResourceName>> {
    return this.partners.getResourceInSelected(resource);
  }

  private loadSiteDetails(partner: Partner, id: number): Observable<Site> {
    return this.requestV2
      .get({
        prefix: partner.getApiUrl(),
        path: "/Sites/{siteId}",
        pathParams: { siteId: id },
      })
      .pipe(
        filter(isExistent),
        map((data) => {
          if (data.id !== id) {
            throw new Error(
              "Site response does not match the requested site ID.",
            );
          }
          return Site.deserialize(data, { partner });
        }),
        take(1),
      );
  }

  /** Sends a freshly saved site out to the site list and the selected site. */
  private publishConfirmedSite(
    data: SharedApiV2.GetSiteDto,
    partner: Partner,
  ): Site {
    const site = Site.deserialize(data, { partner });
    this.summaryUpdates.next(site);
    this.detailUpdates.next(site);
    return site;
  }

  public async get(id: number, partner?: Partner): Promise<Site> {
    const target =
      partner ?? (await firstValueFrom(this.partners.selectedChanges));
    const site = await firstValueFrom(this.loadSiteDetails(target, id));
    this.summaryUpdates.next(site);
    this.detailUpdates.next(site);
    return site;
  }

  public async updateGeneralSettings(
    update: SiteGeneralSettingsUpdate,
  ): Promise<Site> {
    const { id, partner } = this.getRequiredSiteBase(update);

    // The PATCH gives us the whole site back, so a name-only save doesn't need
    // a follow-up GET or a partner reload.
    const data = await firstValueFrom(
      this.requestV2.patch({
        prefix: partner.getApiUrl(),
        path: "/Sites/{siteId}/GeneralSettings",
        pathParams: { siteId: id },
        data: update,
      }),
    );

    const site = this.publishConfirmedSite(data, partner);
    await this.refreshPartnerSitesIfChanged(site, update.base);
    return site;
  }

  public async updateAppointmentsSettings(
    update: SiteAppointmentsSettingsUpdate,
  ): Promise<Site> {
    const { id, partner } = this.getRequiredSiteBase(update);

    await firstValueFrom(
      this.requestV2.patch({
        prefix: partner.getApiUrl(),
        path: "/Sites/{siteId}/AppointmentSettings",
        pathParams: { siteId: id },
        data: update,
      }),
    );

    return await this.get(id, partner);
  }

  public async updateHelpAssistSettings(
    update: SiteHelpAssistSettingsUpdate,
  ): Promise<Site> {
    const { id, partner } = this.getRequiredSiteBase(update);

    await firstValueFrom(
      this.requestV2.patch({
        prefix: partner.getApiUrl(),
        path: "/Sites/{siteId}/HelpAssistSettings",
        pathParams: { siteId: id },
        data: update,
      }),
    );

    return await this.get(id, partner);
  }

  private getRequiredSiteBase(
    update:
      | SiteGeneralSettingsUpdate
      | SiteAppointmentsSettingsUpdate
      | SiteHelpAssistSettingsUpdate,
  ): Site {
    if (!update.base) {
      throw new Error(
        "Cannot update site settings without an existing site to modify.",
      );
    }
    return update.base;
  }

  private async patch(id: number, update: SiteUpdate): Promise<number> {
    const partner =
      update.base?.partner ??
      (await firstValueFrom(this.partners.selectedChanges));
    await firstValueFrom(
      this.request.patch(sitesResource.get(partner).appendId(id), update),
    );
    return id;
  }

  private async post(update: SiteUpdate): Promise<number> {
    const partner = await firstValueFrom(this.partners.selectedChanges);
    const { id } = await firstExistentValueFrom(
      this.requestV2.post({
        prefix: partner.getApiUrl(),
        path: "/Sites",
        data: { serialize: () => update.serializeForCreation() },
      }),
    );
    return id;
  }
}

interface SummaryState {
  /**
   * The `Partner` objects these lists were built from. We compare the objects
   * themselves, not their keys, so a site loaded before a partner reload can't
   * overwrite a row from the newer list.
   */
  readonly loadedPartners: ReadonlySet<Partner>;
  readonly sitesByPartner: ReadonlyMap<string, readonly SiteSummary[]>;
}

interface SelectedSiteLoadState {
  readonly partnerRoleKey: string | null;
  readonly isLoaded: boolean;
}

const emptySummaries: readonly SiteSummary[] = [];

const emptySummaryState: SummaryState = {
  loadedPartners: new Set<Partner>(),
  sitesByPartner: new Map<string, readonly SiteSummary[]>(),
};

function getPartnerRoleKey(partner: Partner): string {
  return `${partner.key.toString()}|${partner.role}`;
}

function isSameSiteAndPartner(
  first: Pick<Site, "id" | "partner">,
  second: Pick<Site, "id" | "partner">,
): boolean {
  return (
    first.id === second.id &&
    first.partner.key.isSame(second.partner.key) &&
    first.partner.role === second.partner.role
  );
}

/**
 * Same site, and same `Partner` object we currently have loaded. Stricter than
 * `isSameSiteAndPartner` on purpose: a partner reload gives us a brand new
 * Partner object, and the selection has to switch over to it so that site
 * updates (which match the same way) keep landing.
 */
function isSameSelectedSite(
  first: Pick<Site, "id" | "partner">,
  second: Pick<Site, "id" | "partner">,
): boolean {
  return first.id === second.id && first.partner === second.partner;
}

/**
 * The "loading"/"not-found" strings are compared by value. Sites are compared
 * by object, so swapping in a site row with a new name won't refetch it, but a
 * partner reload will.
 */
function isSameSelection<T extends SiteSummary | string>(
  previous: T,
  current: T,
): boolean {
  return previous instanceof SiteSummary && current instanceof SiteSummary
    ? isSameSelectedSite(previous, current)
    : previous === current;
}

function applyConfirmedSite(
  sitesByPartner: ReadonlyMap<string, readonly SiteSummary[]>,
  site: Site,
): ReadonlyMap<string, readonly SiteSummary[]> {
  const replaced = new Map(sitesByPartner);
  // The API never lists a decommissioned site, so reading one must not put it
  // back into the choices.
  const isSelectable = site.status !== SiteAvailabilityStatus.Decommissioned;
  const isSameRow = (summary: SiteSummary): boolean =>
    summary.id === site.id && summary.partner.key.isSame(site.partner.key);

  for (const [partnerRoleKey, siteSummaries] of sitesByPartner) {
    const updated = isSelectable
      ? siteSummaries.map((summary) =>
          isSameRow(summary) ? toUpdatedSummary(summary, site) : summary,
        )
      : siteSummaries.filter((summary) => !isSameRow(summary));
    if (
      updated.length !== siteSummaries.length ||
      updated.some((summary, index) => summary !== siteSummaries[index])
    ) {
      replaced.set(partnerRoleKey, sortSiteSummaries(updated));
    }
  }

  // A just-created site has no row yet and the list reload that would add
  // it can fail, so insert it here rather than leave it unselectable.
  const partnerRoleKey = getPartnerRoleKey(site.partner);
  const partnerSites = replaced.get(partnerRoleKey);
  if (
    isSelectable &&
    partnerSites &&
    !partnerSites.some((summary) => summary.id === site.id)
  ) {
    replaced.set(
      partnerRoleKey,
      sortSiteSummaries([...partnerSites, toCreatedSummary(site)]),
    );
  }

  return replaced;
}

function toCreatedSummary(site: Site): SiteSummary {
  return SiteSummary.deserialize(
    {
      id: site.id,
      name: site.name,
      displayName: null,
      masterSiteId: site.number,
      status: site.status,
    },
    { partner: site.partner },
  );
}

function toUpdatedSummary(summary: SiteSummary, site: Site): SiteSummary {
  return summary.name === site.name &&
    summary.number === site.number &&
    summary.status === site.status
    ? summary
    : SiteSummary.deserialize(
        {
          id: summary.id,
          name: site.name,
          displayName: null,
          masterSiteId: site.number,
          status: site.status,
        },
        { partner: summary.partner },
      );
}
